import { css } from 'aphrodite';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DragDropContext, DropResult, Droppable, DroppableProvided } from 'react-beautiful-dnd';
import { HandwrittenCardProvider, IAccount } from '../../../extViewmodels';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { usePostAttachmentCardImage, useTemplateCardCreateLogo } from '../../../queries';
import { DeprecatedCloseButton } from '../../../web/components/DeprecatedCloseButton';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import { CloudUploadIcon } from '../../../web/components/svgs/icons/CloudUploadIcon';
import { TextInput } from '../../../web/components/TextInput';
import { baseStyleSheet } from '../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	onRequestClose: () => void;
	account: IAccount;
}

const inputAccept = '.png, .jpeg, .jpg, .svg';

const fixedMaxHeightInPixels = 1500;
const fixedViewHeight = 500;
const fixedAspectRatio = fixedViewHeight / fixedMaxHeightInPixels;

export const AddCompanyLogoModal = ({ className, onRequestClose, account }: IProps) => {
	const impersonationContext = useMemo(() => ({ account: { id: account.id } }), [account]);
	const [templateName, setTemplateName] = useState('');
	const [productId, setProductId] = useState('');
	const [scale, setScale] = useState(1.0);
	const [originalImageHeight, setOriginalImageHeight] = useState(0);
	const errorMessages = useErrorMessages();

	const [filePreview, setFilePreview] = useState<File>(null);
	const previewUrl = useMemo(() => {
		return filePreview ? URL.createObjectURL(filePreview) : null;
	}, [filePreview]);

	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	const postAccountCardImageMutation = usePostAttachmentCardImage({
		impersonationContext,
		onSuccess: value => {
			const uploadedImage = value.succeeded[0];
			const providerProductIds = {} as Record<HandwrittenCardProvider, string>;
			providerProductIds[account.features.handwrittenCards.provider] = productId;

			postCardTemplateMutation.mutate({
				attachments: [uploadedImage],
				attachmentId: uploadedImage.id,
				name: templateName.trim(),
				providerProductIds,
			});
		},
	});

	const postCardTemplateMutation = useTemplateCardCreateLogo({
		impersonationContext,
		onSuccess: () => {
			URL.revokeObjectURL(previewUrl);
			onRequestClose();
		},
	});

	const onScaleChanged = ({ target }: React.SyntheticEvent<HTMLInputElement>) => {
		const value = parseInt((target as HTMLInputElement).value, 10) / 100.0;
		setScale(value);
	};

	const imgRef = useRef<HTMLImageElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const [isDraggingOver, setIsDraggingOver] = useState(false);

	const onFileChange = (file: File) => {
		if (!file) {
			return; // cancelled the file picker
		}
		setFilePreview(file);
	};

	const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDraggingOver(true);
	};

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDraggingOver(true);
	};

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDraggingOver(false);
	};

	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setIsDraggingOver(false);

		const file = e.dataTransfer.files?.[0];

		if (file) {
			onFileChange(file);
		}
	};

	const onDragEnd = (result: DropResult) => {
		if (!result.destination) {
			return;
		}
	};

	const uploadImage = () => {
		const name = templateName.trim();
		const productIdTrim = productId.trim();
		if (!name) {
			errorMessages.push({
				messages: ['Please enter a name'],
			});
			return;
		}
		if (!productIdTrim) {
			errorMessages.push({
				messages: ['Please enter a product id'],
			});
			return;
		}

		postAccountCardImageMutation.mutate({
			file: filePreview,
			scale,
		});
	};

	const removeImage = () => {
		setFilePreview(null);

		URL.revokeObjectURL(previewUrl);
	};

	const isLoading = postAccountCardImageMutation.isPending || postCardTemplateMutation.isPending;

	const imageHeight = originalImageHeight * scale * fixedAspectRatio || null;

	return (
		<Modal className={`${className ?? ''}`} isOpen>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId='file-upload'>
					{(droppableProvided: DroppableProvided) => {
						return (
							<section>
								{isLoading ? <LoadingSpinner type='large' /> : null}

								{!isLoading && !previewUrl ? (
									<>
										<header className={css(styleSheet.header)}>
											<h2 className={css(styleSheet.uploadModalTitle)}>Add Image</h2>
											<DeprecatedCloseButton onClick={onRequestClose} />
										</header>

										<p className={css(styleSheet.uploadModalRecommendation)}>Recommended file size: 300dpi</p>

										<div
											onDragEnter={handleDragEnter}
											onDragOver={handleDragOver}
											onDragLeave={handleDragLeave}
											onClick={() => {
												inputRef.current?.click();
											}}
											role='button'
											tabIndex={0}
											aria-hidden={false}
											onDrop={handleDrop}
											className={isDraggingOver ? css(styleSheet.dragContainerHover) : css(styleSheet.dragContainer)}
											ref={droppableProvided?.innerRef}
										>
											<input
												ref={inputRef}
												type='file'
												accept={inputAccept}
												style={{ display: 'none' }}
												onChange={ev => {
													if (ev.target.files.length > 0) {
														onFileChange(ev.target.files[0]);
													}
												}}
											/>
											<div className={css(styleSheet.uploadModalInnerWrap)}>
												<CloudUploadIcon />
												<h3 className={css(styleSheet.uploadModalSubTitle)}>Drag and drop image to upload</h3>
												<p className={css(styleSheet.uploadModalDescription)}>Click to browse device</p>
											</div>
										</div>
									</>
								) : null}

								{!isLoading && previewUrl ? (
									<>
										<header>
											<h3>Card Preview</h3>
										</header>

										<label htmlFor='customTemplateCardName' className={css(styleSheet.nameLabel)}>
											Card Template Name
										</label>
										<TextInput
											className={css(styleSheet.nameInput)}
											inputId='customTemplateCardName'
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTemplateName(e.target.value)}
											placeholder='Template name'
											type='text'
											value={templateName}
										/>

										<label htmlFor='providerProductId' className={css(styleSheet.nameLabel)}>
											{account.features.handwrittenCards.provider} Product Id -{' '}
											<a
												href='https://github.com/Real-Magic/Levitate/issues/new?template=design--custom-card-request.md'
												target='_blank'
												rel='noreferrer noopener'
											>
												Need one?
											</a>
										</label>
										<TextInput
											className={css(styleSheet.nameInput)}
											inputId='providerProductId'
											onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProductId(e.target.value)}
											placeholder={`${account.features.handwrittenCards.provider} product id`}
											type='text'
											value={productId}
										/>

										<div className={css(styleSheet.previewImageWrap)}>
											<img
												height={imageHeight}
												ref={imgRef}
												src={previewUrl}
												alt='preview'
												className={css(styleSheet.previewImage)}
												onLoad={() => {
													setOriginalImageHeight(imgRef.current.naturalHeight ?? 0);
												}}
											/>
										</div>
										<footer className={css(styleSheet.footer)}>
											<button
												type='button'
												className={css(baseStyleSheet.ctaButton)}
												onClick={uploadImage}
												disabled={isLoading}
											>
												Save
											</button>

											<button
												type='button'
												className={css(baseStyleSheet.ctaButtonDestructive)}
												onClick={removeImage}
												disabled={isLoading}
											>
												Delete Image
											</button>

											<div className={css(styleSheet.resizeImageContainer)}>
												<span>Resize Image</span>

												<input
													className={css(styleSheet.resizeImageSlider)}
													defaultValue={100}
													max={100}
													min={10}
													onKeyUp={onScaleChanged}
													onMouseUp={onScaleChanged}
													onTouchEnd={onScaleChanged}
													onInput={onScaleChanged}
													type='range'
												/>
											</div>
										</footer>
									</>
								) : null}
							</section>
						);
					}}
				</Droppable>
			</DragDropContext>
		</Modal>
	);
};
